import { GameNode, PlotEnding } from '@/api/node'

export interface TreeNode {
  node: GameNode
  children: TreeNode[]
  depth: number
  /** Number of children that are branch children (from plot_end), rest are main successor */
  branchChildCount: number
  /** Maps each branch child index to its corresponding plot_end ending index */
  branchEndingIndices: number[]
}

export interface FlatTreeItem {
  node: GameNode
  depth: number
  /** 所属分支的父 AI 节点 ID（null 表示在主干上） */
  branchParentId: number | null
  /** 所属分支的结局索引（-1 表示在主干上） */
  branchEndingIndex: number
}

/**
 * Parse plot_end from a node's current_plot_new.
 */
function parsePlotEndings(node: GameNode): PlotEnding[] {
  const plotNew = node.current_plot_new
  if (!plotNew || !plotNew.plot_end) return []
  if (typeof plotNew.plot_end === 'string') return []
  return plotNew.plot_end as PlotEnding[]
}

/**
 * Get the display next_node_id, preferring ui_show_next_node over actual next_node_id.
 */
function getUiNextNodeId(node: GameNode): number | null {
  const ui = node.ui_show_next_node
  if (ui && ui.next_node_id != null) return ui.next_node_id
  return node.next_node_id ?? null
}

/**
 * Get display next_node_ids for each plot ending, preferring ui_show_next_node.plot_end.
 */
function getUiPlotEndingNextIds(node: GameNode): (number | null)[] {
  const ui = node.ui_show_next_node
  const endings = parsePlotEndings(node)
  return endings.map((ending, i) => {
    if (ui?.plot_end?.[i]?.next_node_id != null) return ui.plot_end[i].next_node_id!
    return ending.next_node_id ?? null
  })
}

/**
 * Collect all child node IDs from a node using ui_show_next_node for tree display.
 */
function getChildNodeIds(node: GameNode): number[] {
  const ids: number[] = []
  if (node.node_type === 'fixed') {
    const nextId = getUiNextNodeId(node)
    if (nextId) ids.push(nextId)
  } else if (node.node_type === 'ai') {
    for (const nextId of getUiPlotEndingNextIds(node)) {
      if (nextId) ids.push(nextId)
    }
    const mainNext = getUiNextNodeId(node)
    if (mainNext) ids.push(mainNext)
  }
  return ids
}

/**
 * Find the head node (no incoming edges) of a chapter's node list.
 */
function findHeadNode(nodes: GameNode[]): GameNode | null {
  if (nodes.length === 0) return null
  const nodeIdSet = new Set(nodes.map(n => n.id).filter((id): id is number => id != null))
  const hasIncoming = new Set<number>()
  for (const node of nodes) {
    for (const childId of getChildNodeIds(node)) {
      if (nodeIdSet.has(childId)) hasIncoming.add(childId)
    }
  }
  for (const node of nodes) {
    if (node.id != null && !hasIncoming.has(node.id)) return node
  }
  return nodes.reduce((min, n) =>
    (n.id != null && (min.id == null || n.id < min.id)) ? n : min, nodes[0])
}

/**
 * Build a tree from chapter nodes using the DAG structure.
 */
export function buildNodeTree(nodes: GameNode[]): TreeNode | null {
  if (nodes.length === 0) return null
  const headNode = findHeadNode(nodes)
  if (!headNode) return null

  const nodeMap = new Map<number, GameNode>()
  for (const n of nodes) {
    if (n.id != null) nodeMap.set(n.id, n)
  }

  const visited = new Set<number>()

  function buildSubtree(node: GameNode, depth: number): TreeNode {
    const treeNode: TreeNode = { node, children: [], depth, branchChildCount: 0, branchEndingIndices: [] }
    if (node.id != null) visited.add(node.id)

    if (node.node_type === 'fixed') {
      const nextId = getUiNextNodeId(node)
      if (nextId && nodeMap.has(nextId) && !visited.has(nextId)) {
        treeNode.children.push(buildSubtree(nodeMap.get(nextId)!, depth))
      }
    } else if (node.node_type === 'ai') {
      // 先构建主线后续（标记 visited），防止结局链消费它
      const mainNext = getUiNextNodeId(node)
      let mainNextChild: TreeNode | null = null
      if (mainNext && nodeMap.has(mainNext) && !visited.has(mainNext)) {
        mainNextChild = buildSubtree(nodeMap.get(mainNext)!, depth)
      }
      // 再构建结局分支（深度 +1），结局链遇到已 visited 的节点会自然停止
      // 同时记录每个分支子节点对应的结局索引
      const endingNextIds = getUiPlotEndingNextIds(node)
      endingNextIds.forEach((nextId, endingIdx) => {
        if (nextId && nodeMap.has(nextId) && !visited.has(nextId)) {
          treeNode.children.push(buildSubtree(nodeMap.get(nextId)!, depth + 1))
          treeNode.branchEndingIndices.push(endingIdx)
          treeNode.branchChildCount++
        }
      })
      // 主线后续放在 children 最后，视觉上出现在所有结局分支之后
      if (mainNextChild) {
        treeNode.children.push(mainNextChild)
      }
    }
    return treeNode
  }

  return buildSubtree(headNode, 0)
}

/**
 * Flatten tree into ordered list with depth and branch info.
 * 关键：记录每个节点所属的分支（父 AI 节点 + 结局索引），
 * 这样 getTheoreticalNextNodeId 能区分同深度的不同分支。
 */
export function flattenTree(root: TreeNode | null): FlatTreeItem[] {
  if (!root) return []
  const result: FlatTreeItem[] = []

  function dfs(
    treeNode: TreeNode,
    branchParentId: number | null,
    branchEndingIndex: number
  ) {
    result.push({
      node: treeNode.node,
      depth: treeNode.depth,
      branchParentId,
      branchEndingIndex
    })

    if (treeNode.node.node_type === 'ai') {
      treeNode.children.forEach((child, idx) => {
        if (idx < treeNode.branchChildCount) {
          // 结局分支的子节点：用实际的结局索引标记
          const endingIdx = treeNode.branchEndingIndices[idx]
          dfs(child, treeNode.node.id ?? null, endingIdx)
        } else {
          // AI 节点的 next_node_id 后续（非分支，继承父级分支标记）
          dfs(child, branchParentId, branchEndingIndex)
        }
      })
    } else {
      for (const child of treeNode.children) {
        dfs(child, branchParentId, branchEndingIndex)
      }
    }
  }

  dfs(root, null, -1)
  return result
}

/**
 * 获取理论后续节点 ID。
 *
 * 逻辑：
 * 1. 在同一分支内向前查找下一个同深度节点
 * 2. 如果本分支内没有后续 → 回溯到分支的父节点，找父节点的理论后续
 *
 * "同一分支"通过 branchParentId + branchEndingIndex 判断。
 */
export function getTheoreticalNextNodeId(flatList: FlatTreeItem[], index: number): number | null {
  if (index < 0 || index >= flatList.length) return null

  const current = flatList[index]

  // 向前查找同一分支内的下一个节点
  for (let i = index + 1; i < flatList.length; i++) {
    const item = flatList[i]

    // 遇到更浅的节点 → 当前分支已经结束。
    // 注意：不能把这个更浅节点直接当作后续，它可能是某个祖先 AI 节点的
    // 兄弟结局分支（depth 更浅，但并不在当前分支链上）。直接返回会把不同
    // 分叉错误地连到一起。应跳出循环，走下面"回溯到分支父节点"的逻辑，
    // 求父节点的理论后续（即真正的主线汇合点）。
    if (item.depth < current.depth) {
      break
    }

    // 同深度节点：必须在同一分支内才算后续
    if (item.depth === current.depth) {
      if (item.branchParentId === current.branchParentId &&
          item.branchEndingIndex === current.branchEndingIndex) {
        // 同一分支的下一个节点
        return item.node.id ?? null
      }
      // 不同分支的同深度节点 → 跳过（是兄弟分支）
      continue
    }

    // 更深的节点 → 是当前节点的子分支，跳过
  }

  // 本分支没有后续，回溯到父 AI 节点
  if (current.branchParentId != null) {
    // 找到父 AI 节点在 flatList 中的位置
    const parentIndex = flatList.findIndex(
      item => item.node.id === current.branchParentId
    )
    if (parentIndex >= 0) {
      // 获取父 AI 节点的理论后续（递归回溯）
      return getTheoreticalNextNodeId(flatList, parentIndex)
    }
  }

  return null
}
